<!-- @format -->
<script lang="ts">
  import { createEventDispatcher, onMount, onDestroy } from "svelte";
  import { getStoresContext } from "~/stores";
  import type { StoresKey } from "~/stores";
  import type { ManageAPI } from "~/manage-api/types";
  import { findFirstMatchByStreams, getValidityPeriod } from './util';
  import Tag from '../common/Tag.svelte'
  import OfferView from "~/components/statistics/OfferView.svelte";

  export let offer: ManageAPI.Offer;
  export let request_store_key: StoresKey;
  export let bordered:any;
  export let isStoreOwnPage = false;
  
  // Check for browser environment to prevent SSR issues
  const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
  
  const { reverse_router, stores, scdata, T, lang } = getStoresContext(request_store_key);
  const tag_styles = [ undefined,
    { bg_color: '#fbde31', text_color: '#000', icon: '%' }, //perustarjoukset
    { bg_color: '#5a5594', text_color: '#fff', icon: '!' } //uutuudet
  ];

  $: img_src = offer.images && offer.images[0];
  $: validity = getValidityPeriod(offer.validityStartDate, offer.endDate);
  $: tag = findFirstMatchByStreams(tag_styles, offer);

  $: t = $T;
  $: store = $stores && offer.store?.id && $stores.find((store) => store.id == offer.store?.id);
  $: href = store? $reverse_router.routeStore(store, $scdata) || "" : undefined;

  $: currentLanguage = $lang || "fi"; // Default to Finnish if language is not available

  const dispatch = createEventDispatcher();
  let isVisible = false;
  let isExpanded = false;
  let cardElement: HTMLElement;
  let touchStartX = 0;
  let touchStartY = 0;
  let isScrolling = false;

  function focusOffer(id: number) {
    dispatch("focusOffer", id);
  }

  function handleCardClick(event: MouseEvent) {
    // Only navigate if the click wasn't on the read more button or its children
    if (!(event.target as HTMLElement).closest('.mylly-offer-card-read-more') && 
        !(event.target as HTMLElement).closest('.mylly-offer-card-overlay')) {
      
      // First dispatch the focus event
      focusOffer(offer.id);
      
      // Then navigate to the store page if we have a valid href
      if (href && !isStoreOwnPage) {
        window.location.href = href;
      }
    }
  }

  function toggleReadMore() {
    isExpanded = !isExpanded;
  }

  function handleReadMoreClick(event: MouseEvent) {
    // Prevent the event from bubbling up to the card
    event.stopPropagation();
    toggleReadMore();
  }

  function handleCloseClick(event: MouseEvent) {
    event.stopPropagation();
    isExpanded = false;
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (isExpanded && event.key === 'Escape') {
      isExpanded = false;
    }
  }

  function handleTouchStart(event: TouchEvent) {
    if (isExpanded) {
      // Only handle touch events in the expanded overlay
      const touch = event.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      isScrolling = false;
      
      // Stop propagation only for the overlay to prevent swiper interference
      if ((event.target as HTMLElement).closest('.mylly-offer-card-overlay')) {
        event.stopPropagation();
      }
    }
  }

  function handleTouchMove(event: TouchEvent) {
    if (isExpanded) {
      const touch = event.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartX);
      const deltaY = Math.abs(touch.clientY - touchStartY);
      
      // Determine if user is scrolling vertically (which we should allow)
      // or swiping horizontally (which we should prevent when expanded)
      if (!isScrolling && deltaY > deltaX) {
        isScrolling = true;
      }
      
      // If not scrolling vertically and in expanded mode, prevent swiping
      if (!isScrolling && (event.target as HTMLElement).closest('.mylly-offer-card-overlay')) {
        event.stopPropagation();
      }
    }
  }

  onMount(() => {
    if (!isBrowser) return;
    
    setTimeout(() => {
      isVisible = true;
    }, 10);
    
    window.addEventListener('keydown', handleKeyDown);
    cardElement?.addEventListener('touchstart', handleTouchStart, { passive: true });
    cardElement?.addEventListener('touchmove', handleTouchMove, { passive: false });
    
    return () => {
      if (!isBrowser) return;
      window.removeEventListener('keydown', handleKeyDown);
      cardElement?.removeEventListener('touchstart', handleTouchStart);
      cardElement?.removeEventListener('touchmove', handleTouchMove);
    };
  });

  onDestroy(() => {
    if (!isBrowser) return;
    window.removeEventListener('keydown', handleKeyDown);
    cardElement?.removeEventListener('touchstart', handleTouchStart);
    cardElement?.removeEventListener('touchmove', handleTouchMove);
  });
</script>

<OfferView id={offer.id}>
  <button 
    class="mylly-offer-card" 
    class:bordered 
    class:is-visible={isVisible} 
    class:is-expanded={isExpanded} 
    class:has-link={!!href && !isStoreOwnPage}
    class:has-long-description={!!offer.longDescription}
    on:click={handleCardClick}
    bind:this={cardElement}
    type="button"
  >
    <div class="mylly-offer-card-inner">
      <!-- Image section -->
      <div class="mylly-offer-card-image-wrapper">
        {#if img_src}
          <img src={img_src} alt={offer.title} />
        {/if}
        {#if validity}
          <div class="mylly-validity">{validity}</div>
        {/if}
        {#if tag}
          <Tag {...tag}>{@html tag.icon}</Tag>
        {/if}
      </div>
      
      <!-- Content section with fixed structure -->
      <div class="mylly-offer-card-content-wrapper">
        <!-- Store name - fixed height -->
        <div class="mylly-offer-card-store-container">
          {#if isStoreOwnPage}
            <div class="mylly-offer-card-store-name">{offer.store?.name || ''}</div>
          {:else}
            <a class="mylly-offer-card-store-name" href={href} on:click|stopPropagation>{offer.store?.name || ''}</a>
          {/if}
        </div>
        
        <!-- Title - fixed height -->
        <div class="mylly-offer-card-title">{offer.title}</div>
        
        <!-- Descriptions - fixed heights -->
        <div class="mylly-offer-card-descriptions">
          <div class="mylly-offer-description-1">{offer.description1 || ''}</div>
          <div class="mylly-offer-description-2">{offer.description2 || ''}</div>
          {#if offer.lowestPriceInXDays}
            <div class="mylly-offer-omnibus">{t`lowestPrice30`}: {offer.lowestPriceInXDays}</div>
          {/if}
        </div>
        
        <!-- Spacer to push read more to bottom -->
        <div class="mylly-offer-card-spacer"></div>
        
        <!-- Read more button - always at bottom -->
        <div class="mylly-offer-card-read-more-container">
          {#if offer.longDescription}
            <button 
              class="mylly-offer-card-read-more" 
              on:click={handleReadMoreClick}
              type="button"
              aria-expanded={isExpanded}
            >
              <div class="mylly-offer-card-button">{t`read-more`}</div>
              <span class="mylly-offer-card-small-arrow" class:expanded={isExpanded}></span>
            </button>
          {:else}
            <!-- Empty placeholder to maintain consistent height -->
            <div class="mylly-offer-card-read-more-placeholder"></div>
          {/if}
        </div>
      </div>
      
      <!-- Expanded content overlay -->
      {#if isExpanded && offer.longDescription}
        <button 
          class="mylly-offer-card-overlay" 
          on:click|stopPropagation
          type="button"
          aria-label="Close overlay background"
          lang={currentLanguage}
        >
          <div class="mylly-offer-card-expanded-content">
            <div class="mylly-offer-card-expanded-header">
              <div class="mylly-offer-card-expanded-title" lang={currentLanguage}>{offer.title}</div>
              <button 
                class="mylly-offer-card-close-button" 
                on:click={handleCloseClick}
                aria-label="Close"
              >×</button>
            </div>
            <div class="mylly-offer-card-expanded-body" lang={currentLanguage}>
              {offer.longDescription}
            </div>
          </div>
        </button>
      {/if}
    </div>
  </button>
</OfferView>

<style lang="scss">
  .mylly-validity {
    background-color: #6a44b1;
    color: #fff;
    position: absolute;
    height: 35px;
    bottom: 0;
    right: 0;
    padding: 0 16px;
    border-radius: 10px 0 0 10px;
    font-size: 16px;
    font-weight: 600;
    line-height: 35px;
    text-align: center;
  }
  
  .bordered {
    border-radius: 10px;
    border: 1px solid #101820 !important; /* Use !important to override the button's border: none */
  }
  
  .mylly-offer-card {
    position: relative;
    width: 100%;
    height: 100%;
    color: inherit;
    text-decoration: none;
    background-color: #fff;
    border-radius: 10px;
    transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.3s ease;
    visibility: hidden;
    opacity: 0;
    display: flex;
    flex-direction: column;
    
    /* Remove default button styling */
    border: none;
    padding: 0;
    margin: 0;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    text-align: inherit;
    background: transparent;
    cursor: default;
    appearance: none;
    -webkit-appearance: none;
    
    &.has-link {
      cursor: pointer;
      
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 10px;
        pointer-events: none;
        transition: background-color 0.2s ease;
      }
      
      &:hover::after {
        background-color: rgba(0, 0, 0, 0.03);
      }
    }
    
    &.is-visible {
      visibility: visible;
      opacity: 1;
    }
    
    &.is-expanded {
      z-index: 100 !important;
    }
    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
  }
  
  .mylly-offer-card-inner {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%; /* Explicit width to fix Safari issues */
    overflow: hidden;
    position: relative;
    border-radius: 10px;
    flex: 1;
  }
  
  .mylly-offer-card-image-wrapper {
    position: relative;
    padding-top: 100%;
    background-color: #fff;
    flex-shrink: 0; /* Prevent image from shrinking */
  }

  .mylly-offer-card-image-wrapper img {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .mylly-offer-card-content-wrapper {
    display: flex;
    flex-direction: column;
    flex-grow: 1; /* Allow content to grow and fill available space */
    background: #fff;
    border-radius: 0 0 10px 10px;
    padding: 15px 12px;
    font-size: 16px;
    line-height: 25px;
    text-align: center;
    min-height: 0; /* Allow it to shrink if needed */
    box-sizing: border-box; /* Include padding in height calculation */
  }
  
  .mylly-offer-card-store-container {
    min-height: 30px; /* Minimum height for store name */
    margin-bottom: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .mylly-offer-card-store-name {
    font-weight: bold;
    text-transform: uppercase;
    margin-bottom: 5px;
    line-height: 1.2;
    max-height: 2.4em; /* 2 lines */
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  
  a.mylly-offer-card-store-name {
    color: #101820;
    text-decoration: none;
    position: relative;
    z-index: 2;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  .mylly-offer-card-title {
    min-height: 50px; /* Minimum height for title */
    margin-bottom: 10px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .mylly-offer-card-descriptions {
    display: flex;
    flex-direction: column;
    justify-content: flex-start; /* Align content to the top */
    min-height: 0; /* Allow it to shrink if needed */
  }
  
  .mylly-offer-description-1 {
    font-weight: 600;
    margin-bottom: 5px;
    word-wrap: break-word; /* Allow long words to break */
    display: -webkit-box;
    -webkit-line-clamp: 2; /* Allow up to 2 lines */
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .mylly-offer-description-2 {
    font-style: italic;
    font-size: 15px;
    margin-bottom: 5px;
    display: -webkit-box;
    -webkit-line-clamp: 2; /* Allow up to 2 lines */
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .mylly-offer-omnibus {
    font-style: italic;
    font-size: 14px;
    margin-bottom: 10px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .mylly-offer-card-spacer {
    flex-grow: 1; /* Take up all available space */
    min-height: 0; /* Allow it to shrink completely if needed */
    margin-top: auto; /* Push the spacer to the bottom */
  }
  
  .mylly-offer-card-read-more-container {
    margin-top: 10px;
    min-height: 34px; /* Minimum height for read more button */
    display: flex;
    justify-content: center;
    align-items: center; /* Center vertically */
  }
  
  .mylly-offer-card-read-more {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    z-index: 2; /* Ensure it's above the card clickable area */
    background: transparent;
    border: none;
    padding: 0;
    margin: 0;
    font-family: inherit;
    font-size: inherit;
    color: inherit;
    text-align: inherit;
  }
  
  .mylly-offer-card-button {
    padding: 0 8px;
    font-family: var(--font-mono);
    display: inline-block;
  }
  
  .mylly-offer-card-small-arrow {
    background: url('/theme/mylly2023/images/small-arrow.svg');
    width: 17px;
    height: 14px;
    -webkit-transform: rotate(-90deg);
    -moz-transform: rotate(-90deg);
    -ms-transform: rotate(-90deg);
    -o-transform: rotate(-90deg);
    transform: rotate(-90deg);
    transition: 0.2s ease;
    display: inline-block;
    vertical-align: middle;
    
    &.expanded {
      -webkit-transform: rotate(90deg);
      -moz-transform: rotate(90deg);
      -ms-transform: rotate(90deg);
      -o-transform: rotate(90deg);
      transform: rotate(90deg);
    }
  }
  
  .mylly-offer-card-read-more-placeholder {
    height: 14px; /* Match the height of the read more button */
  }
  
  /* Overlay and expanded content styles */
  .mylly-offer-card-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.98); /* Slightly more opaque */
    z-index: 200;
    border-radius: 10px; /* Match the card's border radius exactly */
    display: flex;
    flex-direction: column;
    padding: 15px;
    animation: fadeIn 0.2s ease-in-out;
    overflow: hidden; /* Prevent content from overflowing */
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15); /* Add shadow for better visibility */
    box-sizing: border-box; /* Include padding in size calculation */
    max-width: 100%;
    color: #101820; /* Explicit text color to override iOS Safari defaults */
    color-scheme: light; /* Force light mode rendering regardless of system settings */
    -webkit-text-fill-color: #101820; /* Ensure text color is applied in iOS Safari */
    
    /* Remove default button styling */
    border: none;
    margin: 0;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    text-align: inherit;
    cursor: default;
    appearance: none;
    -webkit-appearance: none;
  }
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  .mylly-offer-card-expanded-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow: hidden;
    max-height: 100%; /* Ensure it doesn't overflow */
    color: #101820; /* Explicit text color */
  }
  
  .mylly-offer-card-expanded-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
    flex-wrap: nowrap; /* Prevent wrapping */
    min-height: 30px; /* Ensure minimum height for header */
    width: 100%;
    color: #101820; /* Explicit text color */
  }
  
  .mylly-offer-card-expanded-title {
    font-weight: bold;
    font-size: 16px; /* Reduced from 18px to help with wrapping */
    text-align: left;
    word-wrap: break-word; /* Allow long words to break */
    overflow-wrap: break-word; /* Modern browsers */
    max-width: calc(100% - 40px); /* Increased space for the close button */
    flex: 1; /* Take up available space */
    hyphens: auto;
    -webkit-hyphens: auto;
    -ms-hyphens: auto;
    -moz-hyphens: auto;
    overflow: hidden;
    line-height: 1.3;
    color: #101820; /* Explicit text color */
    -webkit-text-fill-color: #101820; /* iOS Safari specific */
  }
  
  .mylly-offer-card-close-button {
    background: none;
    border: none;
    font-size: 24px;
    line-height: 1;
    cursor: pointer;
    padding: 5px;
    margin: -5px;
    color: #666;
    min-width: 34px;
    min-height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: absolute;
    top: 10px;
    right: 10px;  
    z-index: 20;
    border-radius: 50%;
    
    &:hover {
      color: #000;
      background-color: rgba(0, 0, 0, 0.05);
    }
    
    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(106, 68, 177, 0.5);
    }
  }
  
  .mylly-offer-card-expanded-body {
    flex-grow: 1;
    overflow-y: auto;
    text-align: left;
    font-size: 15px;
    line-height: 1.5;
    padding-right: 5px;
    padding-bottom: 10px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    -webkit-hyphens: auto;
    -ms-hyphens: auto;
    -moz-hyphens: auto;
    color: #101820; /* Explicit text color */
    -webkit-text-fill-color: #101820; /* iOS Safari specific */
    
    /* Custom scrollbar for webkit browsers */
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #6a44b1;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb:hover {
      background: #5a3a9e;
    }
  }
</style>
